import * as React from 'react'

import { i18n } from '@thg-commerce/enterprise-core'
import { BestSellers } from '@thg-commerce/enterprise-widget-best-sellers'
import {
  GlobalBuyingRightNow,
  GlobalBuyingRightNowManualProductUrl,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { ProductBlock } from '@thg-commerce/enterprise-graphql/aurora-schema'

import { ProductProps } from '@thg-commerce/enterprise-widget-best-sellers/src/BestSellers'
import { BaseWidgetProps } from '../types'

export interface BuyingRightNowWidgetRendererProps
  extends Omit<
    (GlobalBuyingRightNow | GlobalBuyingRightNowManualProductUrl) &
      BaseWidgetProps,
    'sku' | 'skutwo' | 'skuthree' | 'skufour' | 'query'
  > {
  skuVariantOne?: ProductProps
  skuVariantTwo?: ProductProps
  skuVariantThree?: ProductProps
  skuVariantFour?: ProductProps
  productBlockOne?: ProductBlock
  productBlockTwo?: ProductBlock
  productBlockThree?: ProductBlock
  productBlockFour?: ProductBlock
  url1?: string
  url2?: string
  url3?: string
  url4?: string
  widgetIndex: number
}

export const BuyingRightNowRenderer = (
  props: BuyingRightNowWidgetRendererProps,
) => {
  const rrpText = i18n('product.price.rrp.text')

  const productBlocks = [
    props.productBlockOne
      ? {
          ...props.productBlockOne,
          title: {
            value: props.productTextOne || props.productBlockOne.title,
          },
          price: {
            ...props.productBlockOne.price,
            rrpText,
          },

          marketedSpecialOffer: props.productBlockOne.marketedSpecialOffer
            ? {
                ...props.productBlockOne.marketedSpecialOffer,
                title: props.productBlockOne.marketedSpecialOffer.titleText,
                description:
                  props.productBlockOne.marketedSpecialOffer.descriptionText,
                onlyDisplayOfferBadge: true,
              }
            : undefined,
          review: props.productBlockOne.reviews && {
            starRating: props.productBlockOne.reviews.averageScore,
            numberOfReviews: props.productBlockOne.reviews.total,
          },
        }
      : null,
    props.productBlockTwo
      ? {
          ...props.productBlockTwo,
          title: {
            value: props.productTextTwo || props.productBlockTwo.title,
          },
          price: {
            ...props.productBlockTwo.price,
            rrpText,
          },
          marketedSpecialOffer: props.productBlockTwo.marketedSpecialOffer
            ? {
                ...props.productBlockTwo.marketedSpecialOffer,
                title: props.productBlockTwo.marketedSpecialOffer.titleText,
                description:
                  props.productBlockTwo.marketedSpecialOffer.descriptionText,
                onlyDisplayOfferBadge: true,
              }
            : undefined,
          review: props.productBlockTwo.reviews && {
            starRating: props.productBlockTwo.reviews.averageScore,
            numberOfReviews: props.productBlockTwo.reviews.total,
          },
        }
      : null,
    props.productBlockThree
      ? {
          ...props.productBlockThree,
          title: {
            value: props.productTextThree || props.productBlockThree.title,
          },
          price: {
            ...props.productBlockThree.price,
            rrpText,
          },
          marketedSpecialOffer: props.productBlockThree.marketedSpecialOffer
            ? {
                ...props.productBlockThree.marketedSpecialOffer,
                title: props.productBlockThree.marketedSpecialOffer.titleText,
                description:
                  props.productBlockThree.marketedSpecialOffer.descriptionText,
                onlyDisplayOfferBadge: true,
              }
            : undefined,
          review: props.productBlockThree.reviews && {
            starRating: props.productBlockThree.reviews.averageScore,
            numberOfReviews: props.productBlockThree.reviews.total,
          },
        }
      : null,
    props.productBlockFour
      ? {
          ...props.productBlockFour,
          title: {
            value: props.productTextFour || props.productBlockFour.title,
          },
          price: {
            ...props.productBlockFour.price,
            rrpText,
          },
          marketedSpecialOffer: props.productBlockFour.marketedSpecialOffer
            ? {
                ...props.productBlockFour.marketedSpecialOffer,
                title: props.productBlockFour.marketedSpecialOffer.titleText,
                description:
                  props.productBlockFour.marketedSpecialOffer.descriptionText,
                onlyDisplayOfferBadge: true,
              }
            : undefined,
          review: props.productBlockFour.reviews && {
            starRating: props.productBlockFour.reviews.averageScore,
            numberOfReviews: props.productBlockFour.reviews.total,
          },
        }
      : null,
  ].filter((productBlock) => productBlock !== null)

  const products: ProductProps[] | ProductBlock[] = productBlocks.length
    ? productBlocks
    : [
        props.skuVariantOne && {
          ...props.skuVariantOne,
          title: {
            value: props.productTextOne || props.skuVariantOne.title.value,
          },
          url: !!props.url1 ? props.url1 : props.skuVariantOne.url,
          price: {
            rrpText,
            ...props.skuVariantOne.price,
          } as ProductProps['price'],
        },
        props.skuVariantTwo && {
          ...props.skuVariantTwo,
          title: {
            value: props.productTextTwo || props.skuVariantTwo.title.value,
          },
          url: !!props.url2 ? props.url2 : props.skuVariantTwo.url,
          price: {
            rrpText,
            ...props.skuVariantTwo.price,
          } as ProductProps['price'],
        },
        props.skuVariantThree && {
          ...props.skuVariantThree,
          title: {
            value: props.productTextThree || props.skuVariantThree.title.value,
          },
          url: !!props.url3 ? props.url3 : props.skuVariantThree.url,
          price: {
            rrpText,
            ...props.skuVariantThree.price,
          } as ProductProps['price'],
        },
        props.skuVariantFour && {
          ...props.skuVariantFour,
          title: {
            value: props.productTextFour || props.skuVariantFour.title.value,
          },
          url: !!props.url4 ? props.url4 : props.skuVariantFour.url,
          price: {
            rrpText,
            ...props.skuVariantFour.price,
          } as ProductProps['price'],
        },
      ].filter((product) => product)

  return (
    <BestSellers
      title={!!props.title ? { text: props.title } : undefined}
      widgetIndex={props.widgetIndex}
      productBlocks={products}
      displayQuickBuyButton={false}
    />
  )
}
